import { IconButton } from "@components/button";
import { CloseIcon } from "@components/icon";
import {
    ISmartFieldProps,
    SmartField,
    TCustomRenderWrapper,
    TFieldProps
} from "@components/smart/smartField/SmartField";
import { IGetControlDataRetValue } from "@components/smart/smartField/SmartFieldUtils";
import {
    HighlightedLabelMatch,
    HighlightedLabelText,
    LabelInputStyled,
    ListRowWrapper
} from "@components/smart/smartField/SmartListField.styles";
import BindingContext from "@odata/BindingContext";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import { isDefined } from "@utils/general";
import { splitStringToSearchedParts } from "@utils/string";
import React, { ComponentType } from "react";
import { withTranslation } from "react-i18next";

import { BasicInputSizes, IconSize, LabelStatus, TextAlign } from "../../../enums";
import { FormStorage } from "../../../views/formView/FormStorage";

interface IProps extends ISmartFieldProps {
    onRemoveField?: (bc: BindingContext) => void;
    hasMatch?: boolean;
    canRemove?: boolean;
    highlightTerm?: string;
    isSearchResultActive?: boolean;
}

/**
 * Smart field, which renders as a disabled field with a label as content and normal
 * field next to it according to definition. There is a delete button at the end of the row, which triggers onRemoveField
 * callback when clicked. This field is used for rendering list of fields, which can be virtualized.
 */
class SmartListField extends SmartField<IProps> {

    get customRenderWrapper(): TCustomRenderWrapper {
        return this.renderListField;
    }

    get fieldProps(): TFieldProps {
        return {
            ...(this.props.fieldProps ?? {}),
            labelStatus: LabelStatus.Removed,
            width: BasicInputSizes.M,
            isWithoutRightMargin: true,
            isSharpLeft: true
        };
    }

    handleRemove = (): void => {
        this.props.onRemoveField(this.props.bindingContext);
    }

    renderHighlightedLabel = (label: string): React.ReactElement => {
        if (!this.props.hasMatch) {
            return <HighlightedLabelText>{label}</HighlightedLabelText>;
        }

        const parts = splitStringToSearchedParts(label, this.props.highlightTerm);
        if (!parts?.root) {
            return <HighlightedLabelText>{label}</HighlightedLabelText>;
        }

        return (
            <HighlightedLabelText>
                {parts.prefix}
                <HighlightedLabelMatch $isActive={!!this.props.isSearchResultActive}>{parts.root}</HighlightedLabelMatch>
                {parts.suffix}
            </HighlightedLabelText>
        );
    };

    renderListField(field: React.ReactNode, info: IFieldInfo, data: IGetControlDataRetValue): React.ReactNode {
        const storage = this.props.storage as FormStorage;

        const label = [info.metadata?.id, info.label].filter(Boolean).join(" | ");

        // if visibility is set, field is dependent on other field, and it's not customizable by user.
        const isDependent = isDefined(info.isVisible);
        const { isRequired } = data.props ?? {};
        const isReadOnly = data.props.isReadOnly || storage.isReadOnly;

        const _canRemove = this.props.canRemove ?? (!isDependent && !isRequired);

        return (
            <ListRowWrapper>
                <LabelInputStyled contentBefore={this.renderHighlightedLabel(label)}
                                  id={`${this.props.bindingContext.getFullPath(false)}-label`}
                                  value={label}
                                  isDisabled
                                  isSharpRight
                                  textAlign={TextAlign.Left}/>
                {field}
                {!isReadOnly && (
                    <IconButton title={this.props.t("Common:General.Remove")}
                                onClick={this.handleRemove}
                                tabIndex={-1} // prevent tabbing -> user should tab directly to next field, not to remove button
                                isDisabled={!_canRemove}
                                isDecorative
                                isTransparent>
                        <CloseIcon width={IconSize.S} height={IconSize.S}/>
                    </IconButton>
                )}
            </ListRowWrapper>
        );
    }
}

const HOC = withTranslation(["Common", "Components"], { withRef: true })(SmartListField) as ComponentType<IProps>;
HOC.displayName = "SmartListFieldWithTranslation";

export default HOC;